import { useEffect, useRef, useState } from "react";

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Drives the two scroll-coupled effects from the design that aren't expressible in pure CSS:
 *  - `scrolled`: true once the page is scrolled past 40px (the nav solidifies).
 *  - parallax: elements carrying `data-px` are translated on the Y axis as they pass the viewport.
 *
 * Reveal-on-scroll is handled separately by the <Reveal> component (IntersectionObserver).
 * All motion is disabled under `prefers-reduced-motion`.
 */
export function useHomeMotion(): { scrolled: boolean } {
  const [scrolled, setScrolled] = useState(false);
  const tickingRef = useRef(false);

  useEffect(() => {
    const reduce = prefersReducedMotion();

    const apply = () => {
      tickingRef.current = false;
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      setScrolled(y > 40);

      if (reduce) return;
      const vh = window.innerHeight;
      document.querySelectorAll<HTMLElement>("[data-px]").forEach((el) => {
        const rect = el.getBoundingClientRect();
        const mid = rect.top + rect.height / 2 - vh / 2;
        const depth = parseFloat(el.dataset.px ?? "0.08") || 0.08;
        el.style.transform = `translate3d(0, ${(mid * -depth).toFixed(1)}px, 0)`;
      });
    };

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      window.requestAnimationFrame(apply);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    apply();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return { scrolled };
}
